import { useSessionContext } from "modules/Session/SessionContext";
import { useEffect, useRef } from "react";
import useAppStore from "store/store";

interface PageCardProps {
    html: string;
    htmlData?: any;
}

const HtmlInFrame = ({ html, htmlData }: PageCardProps) => {

	const { postSession } = useSessionContext()
	const addVisibleGroup = useAppStore((state) => state.addVisibleGroup);
	const removeVisibleGroup = useAppStore((state) => state.removeVisibleGroup);
	const visibleGroups = useAppStore((state) => state.visibleGroups);

	const iframeRef = useRef(null);

	// Track which elements are already added
	const addedRef = useRef(new Set());

	// Track timeouts per element
	const timeoutMap = useRef(new Map());

	useEffect(() => {
		postSession({
			event: 'guest_scroll',
			data: {
				visible: visibleGroups,
			}
		});
	}, [ visibleGroups ]);
	
	useEffect(() => {
		const iframe = iframeRef.current;

		const handleIframeLoad = () => {
			const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
			const iframeWin = iframe.contentWindow;

			if (!iframeDoc) return;

			// Add a click listener inside the iframe
			iframeDoc.addEventListener('click', ( e ) => {
				// Check if the target contains 'attr-scroll'
				if (e.target && e.target.hasAttribute('attr-scroll')) {
					const scrollTarget = e.target.getAttribute('attr-scroll');

					// Log the click event
					postSession({
						event: 'guest_menu_click',
						data: {
							target: scrollTarget,
						}
					});
				}
			});

			//
     	 	const targets = iframeDoc.querySelectorAll("[attr-scroll-to]");
			if ( targets.length && iframeWin.IntersectionObserver) {
				const observer = new iframeWin.IntersectionObserver(
					(entries) => {
						entries.forEach((entry) => {

							const element = entry.target;
							const scrollTo = element.getAttribute('attr-scroll-to');

							if (entry.isIntersecting) {
								// Start a 2-second timer
								if (!timeoutMap.current.has( scrollTo )) {
									const timeoutId = setTimeout(() => {
										if (!addedRef.current.has( scrollTo )) {
											addedRef.current.add( scrollTo );

											// Add to the store
											addVisibleGroup( scrollTo );
										}

										timeoutMap.current.delete( scrollTo );
									}, 2000);

									timeoutMap.current.set( scrollTo, timeoutId);
								}
							} else {
								// Cancel the timer if the element goes out of view early
								if (timeoutMap.current.has(scrollTo)) {
									clearTimeout(
										timeoutMap.current.get(scrollTo)
									);
									timeoutMap.current.delete(scrollTo);
								}

								// Remove from "already added" so it can be re-added later
								addedRef.current.delete(scrollTo);

								// And remove from the store
								removeVisibleGroup(scrollTo);
							}
						});
					},
					{
						root: null, // iframe's own viewport
						threshold: 0.5, // visible when at least 50% of the div is in view
					}
				);

				targets.forEach((el) => observer.observe(el));
			}
		};

		// Attach load listener
		if (iframe) {
			iframe.addEventListener('load', handleIframeLoad);
		}

		// Cleanup
		return () => {
			if (iframe) {
				iframe.removeEventListener('load', handleIframeLoad);
			}
		};
	}, []);

	return (
		<>
			<title>{ htmlData?.title || '' }</title>

			<iframe 
				ref={iframeRef}
				srcDoc={ html } 
			/>
		</>
	);
};

export default HtmlInFrame;
